import { useEffect } from 'react';
import {
  Box,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Image,
  Stack,
} from '@customerio/pluma-components/react';
import type { Permission } from '@customerio/hydra-permissions';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import { useAnalytics } from '@/services/analytics';
import { SampleWorkspaceViewButton } from './sample-workspace-view-button';

export type WelcomeEmptyStateProps = {
  /**
   * Names the surface in the empty-state analytics (`Empty State`), so every page that shows
   * this reports into the same two events.
   */
  label: string;
  title: string;
  description: string;
  actionLabel: string;
  actionHref: string;
  /** The permission the action needs. Without it the action is disabled, with a tooltip saying why. */
  actionPermission?: Permission;
  image: string;
  /** Where "View sample workspace" goes: this page, in the sample workspace. */
  sampleWorkspaceHref: (sampleWorkspaceId: number) => string;
  /** The page name the sample-workspace click reports. */
  sampleWorkspaceRouteName: string;
};

/**
 * The first-run page a list shows when the workspace has nothing in it yet: a pitch, the
 * action that creates the first item, a way into the sample workspace, and an illustration.
 */
export function WelcomeEmptyState({
  label,
  title,
  description,
  actionLabel,
  actionHref,
  actionPermission,
  image,
  sampleWorkspaceHref,
  sampleWorkspaceRouteName,
}: WelcomeEmptyStateProps) {
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  const isActionDisabled = actionPermission == null ? false : !hasPermission(actionPermission);

  useEffect(() => {
    void track('logs', 'emptyStateViewed', { 'Empty State': label });
  }, [track, label]);

  return (
    <Stack gap="400" alignInline="center">
      <EmptyState variant="promotion">
        <EmptyStateTitle>{title}</EmptyStateTitle>
        <EmptyStateDescription>{description}</EmptyStateDescription>
        <EmptyStateActions>
          <ViewerRestrictedTooltip readOnly={isActionDisabled}>
            {(context) => (
              <Button
                variant="primary"
                isDisabled={isActionDisabled}
                href={isActionDisabled ? undefined : actionHref}
                icon="chevron-right"
                iconPosition="trailing"
                onClick={() => void track('logs', 'emptyStateActionClicked', { 'Empty State': label })}
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
              >
                {actionLabel}
              </Button>
            )}
          </ViewerRestrictedTooltip>
          <SampleWorkspaceViewButton href={sampleWorkspaceHref} routeName={sampleWorkspaceRouteName} />
        </EmptyStateActions>
      </EmptyState>

      <Box w="full" style={{ maxWidth: '889px' }}>
        <Image src={image} alt="" />
      </Box>
    </Stack>
  );
}
